"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { CostComparisonDrawer } from "@/components/pricing/cost-comparison-drawer";
import { ComparisonContent } from "@/components/pricing/comparison-content";
import { TailoredComparisonForm } from "@/components/pricing/tailored-comparison-form";
import Link from "next/link";
import {
  COMPARISONS,
  CANVAS_SUBTITLE,
  DRAWER_VAT_LINE,
} from "@/lib/pricing-comparison-data";
import {
  COMPARISON_PARAM,
  OPEN_COMPARISON_EVENT,
  isComparisonTarget,
  type ComparisonTarget,
  type OpenComparisonDetail,
} from "@/components/pricing/comparison-events";

const EXIT_MS = 320;

function readParam(): ComparisonTarget | null {
  if (typeof window === "undefined") return null;
  const v = new URLSearchParams(window.location.search).get(COMPARISON_PARAM);
  return isComparisonTarget(v) ? v : null;
}

function writeParam(target: ComparisonTarget | null) {
  const url = new URL(window.location.href);
  if (target) url.searchParams.set(COMPARISON_PARAM, target);
  else url.searchParams.delete(COMPARISON_PARAM);
  window.history.replaceState(window.history.state, "", url);
}

/**
 * Single mount point for the worked cost comparison drawers.
 *
 * Opens on the {@link OPEN_COMPARISON_EVENT} custom event or on a
 * `?comparison=small|team|tailored` query parameter (deep link / back-forward).
 * `active` is the mounted target; `closing` keeps it mounted through the
 * slide-out. Focus returns to the element that opened the drawer.
 */
export function ComparisonManager() {
  const [active, setActive] = useState<ComparisonTarget | null>(null);
  const [closing, setClosing] = useState(false);
  const triggerRef = useRef<HTMLElement | null>(null);
  const exitTimer = useRef<number | null>(null);

  const clearExit = () => {
    if (exitTimer.current) {
      window.clearTimeout(exitTimer.current);
      exitTimer.current = null;
    }
  };

  const openTarget = useCallback(
    (target: ComparisonTarget, rememberTrigger: boolean) => {
      clearExit();
      if (rememberTrigger) {
        triggerRef.current =
          (document.activeElement as HTMLElement | null) ?? null;
      }
      setClosing(false);
      setActive((current) => (current === target ? current : target));
    },
    [],
  );

  const finishClose = useCallback((restoreFocus: boolean) => {
    setClosing(true);
    clearExit();
    exitTimer.current = window.setTimeout(() => {
      setActive(null);
      setClosing(false);
      exitTimer.current = null;
      if (restoreFocus) {
        const el = triggerRef.current;
        triggerRef.current = null;
        el?.focus?.();
      }
    }, EXIT_MS);
  }, []);

  const close = useCallback(() => {
    writeParam(null);
    finishClose(true);
  }, [finishClose]);

  // Custom-event trigger from the pricing cards.
  useEffect(() => {
    const onOpen = (e: Event) => {
      const detail = (e as CustomEvent<OpenComparisonDetail>).detail;
      if (detail && isComparisonTarget(detail.target)) {
        writeParam(detail.target);
        openTarget(detail.target, true);
      }
    };
    window.addEventListener(OPEN_COMPARISON_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_COMPARISON_EVENT, onOpen);
  }, [openTarget]);

  // Deep link on first load + browser back/forward. The initial read is
  // deferred to a timer so it is not a synchronous setState during render.
  useEffect(() => {
    const onPop = () => {
      const target = readParam();
      if (target) openTarget(target, false);
      else finishClose(false);
    };
    const id = window.setTimeout(() => {
      const target = readParam();
      if (target) openTarget(target, false);
    }, 0);
    window.addEventListener("popstate", onPop);
    return () => {
      window.clearTimeout(id);
      window.removeEventListener("popstate", onPop);
      clearExit();
    };
  }, [openTarget, finishClose]);

  if (!active) return null;

  const worked = active === "tailored" ? null : COMPARISONS[active];

  return (
    <CostComparisonDrawer
      present={!closing}
      onClose={close}
      labelledById="cost-comparison-title"
      describedById={worked ? "cost-comparison-subtitle" : undefined}
      title={worked ? worked.drawerTitle : "Request a tailored cost comparison"}
      subtitle={worked ? CANVAS_SUBTITLE : undefined}
      footer={
        worked ? (
          <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-2 text-xs">
            <span className="text-on-surface-variant/70">{DRAWER_VAT_LINE}</span>
            <span className="flex items-center gap-5">
              <Link
                href="/pricing/methodology"
                className="font-semibold text-brand-electric hover:underline"
              >
                Methodology
              </Link>
              <Link
                href="/request-demo"
                className="font-semibold text-brand-electric hover:underline"
              >
                Request a Demo
              </Link>
            </span>
          </div>
        ) : undefined
      }
    >
      {worked ? (
        <ComparisonContent data={worked} />
      ) : (
        <TailoredComparisonForm />
      )}
    </CostComparisonDrawer>
  );
}
